/**
 * PDF2 Slide 2 - Visitas e Via de Contato
 * Endpoint: /api/v1/visitas/dashboards/visitas-contato/
 * 
 * Filtros:
 * - Visitas por Mês: ANO apenas
 * - % de Revisitas: ANO + MÊS
 * - Via de Contato: ANO + MÊS
 */
import { useState, useEffect } from "react";
import { Card, Row, Col, Spinner, Alert, Badge } from "react-bootstrap";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import api from "../../../services/api";
import { traduzirMesesEmDados, MESES_COMPLETOS } from "./utils/mesesUtils";

const Slide6VisitasContato = ({ programaId, filters }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const ano = filters?.year || new Date().getFullYear();
  const mes = filters?.month;

  useEffect(() => {
    let url = `/visitas/dashboards/visitas-contato/?programa_id=${programaId}&ano=${ano}`;
    if (mes !== null && mes !== undefined) {
      url += `&mes=${mes}`;
    }
    
    api.get(url)
      .then(res => {
        if (res.data) {
          setData(res.data);
        } else {
          setData({ total_visitas: 0, visitas_mensal: [], revisitas: [], canais_contato: [] });
        }
      })
      .catch((err) => {
        console.error('[Slide6] API error:', err.response?.data || err.message);
        setError(err.response?.data?.error || err.message);
      })
      .finally(() => setLoading(false));
  }, [programaId, ano, mes]);

  const labelAno = `Ano: ${ano}`;
  const labelAnoMes = mes ? `${MESES_COMPLETOS[mes]} ${ano}` : `Ano: ${ano}`;

  if (loading) return <Spinner animation="border" className="d-block mx-auto my-4" />;

  if (error) {
    return (
      <Alert variant="warning" className="m-3">
        <strong>Seção 5 - Visitas e Via de Contato:</strong> {error}
      </Alert>
    );
  }

  const visitasArr = Array.isArray(data?.visitas_mensal) ? data.visitas_mensal : [];
  const revisitasArr = Array.isArray(data?.revisitas) ? data.revisitas : [];
  const canaisArr = Array.isArray(data?.canais_contato) ? data.canais_contato : [];
  if (data && (data.visitas_mensal !== undefined && !Array.isArray(data.visitas_mensal))) {
    console.warn('[Slide6] visitas_mensal não é array:', data.visitas_mensal);
  }
  if (data && (data.revisitas !== undefined && !Array.isArray(data.revisitas))) {
    console.warn('[Slide6] revisitas não é array:', data.revisitas);
  }
  if (data && (data.canais_contato !== undefined && !Array.isArray(data.canais_contato))) {
    console.warn('[Slide6] canais_contato não é array:', data.canais_contato);
  }
  const hasVisitas = visitasArr.length > 0;
  const hasRevisitas = revisitasArr.length > 0;
  const hasCanais = canaisArr.length > 0;

  const visitasTraduzidas = traduzirMesesEmDados(visitasArr);

  const pieData = hasRevisitas ? [
    {
      id: "Única Visita",
      label: "Única Visita",
      value: revisitasArr.find(r => r?.id === "Única Visita")?.value || 0,
      color: "#5b9bd5"
    },
    {
      id: "Revisita",
      label: "Revisita",
      value: revisitasArr.find(r => r?.id === "Revisita")?.value || 0,
      color: "#ed7d31"
    }
  ] : [];

  const CenteredMetric = ({ centerX, centerY }) => (
    <>
      <text
        x={centerX}
        y={centerY - 10}
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize: "26px", fontWeight: "bold", fill: "#333" }}
      >
        {data?.total_visitas || 0}
      </text>
      <text
        x={centerX}
        y={centerY + 15}
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize: "11px", fill: "#666" }}
      >
        Total
      </text>
    </>
  );

  return (
    <>
      {/* Linha 1: Visitas Mensais + Revisitas */}
      <Row className="align-items-stretch">
        {/* Visitas por Mês - Filtro: ANO apenas */}
        <Col md={8} className="mb-4">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Header className="bg-white fw-bold border-0 d-flex justify-content-between align-items-center">
              <span>Visitas por Mês</span>
              <Badge bg="secondary" className="fw-normal">{labelAno}</Badge>
            </Card.Header>
            <Card.Body style={{ height: "280px" }}>
              {hasVisitas ? (
                <ResponsiveBar
                  data={visitasTraduzidas}
                  keys={["visitas"]}
                  indexBy="mes"
                  margin={{ top: 10, right: 10, bottom: 30, left: 40 }}
                  padding={0.3}
                  colors={["#ed7d31"]}
                  labelTextColor="#fff"
                  axisBottom={{ tickSize: 0, tickPadding: 8 }}
                  axisLeft={{ tickSize: 0, tickPadding: 8 }}
                  enableGridY={true}
                />
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <span className="text-muted">Sem registros disponíveis</span>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* % de Revisitas - Filtro: ANO + MÊS */}
        <Col md={4} className="mb-4">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Header className="bg-white fw-bold border-0 d-flex justify-content-between align-items-center">
              <span>% de Revisitas</span>
              <Badge bg="primary" className="fw-normal">{labelAnoMes}</Badge>
            </Card.Header>
            <Card.Body style={{ height: "280px" }}>
              {hasRevisitas ? (
                <ResponsivePie
                  data={pieData}
                  margin={{ top: 15, right: 15, bottom: 60, left: 15 }}
                  innerRadius={0.6}
                  colors={{ datum: "data.color" }}
                  enableArcLinkLabels={false}
                  arcLabelsTextColor="#fff"
                  layers={['arcs', 'arcLabels', 'legends', CenteredMetric]}
                  legends={[
                    {
                      anchor: "bottom",
                      direction: "row",
                      translateY: 40,
                      itemWidth: 80,
                      itemHeight: 18,
                      symbolSize: 10,
                      symbolShape: "circle"
                    }
                  ]}
                />
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <span className="text-muted">Sem registros disponíveis</span>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Linha 2: Via de Contato - Filtro: ANO + MÊS */}
      <Row>
        <Col md={12} className="mb-4">
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white fw-bold border-0 d-flex justify-content-between align-items-center">
              <span>Via de Contato</span>
              <Badge bg="primary" className="fw-normal">{labelAnoMes}</Badge>
            </Card.Header>
            <Card.Body style={{ height: "300px" }}>
              {hasCanais ? (
                <ResponsiveBar
                  data={canaisArr}
                  keys={["total"]}
                  indexBy="canal"
                  layout="vertical"
                  margin={{ top: 20, right: 20, bottom: 60, left: 40 }}
                  padding={0.3}
                  colors={["#5b9bd5"]}
                  labelTextColor="#fff"
                  axisBottom={{
                    tickSize: 0,
                    tickPadding: 10,
                    tickRotation: -30
                  }}
                  axisLeft={{ tickSize: 0, tickPadding: 10 }}
                  enableGridY={true}
                  enableGridX={false}
                />
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <span className="text-muted">Sem registros disponíveis</span>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default Slide6VisitasContato;
